import { useQueries, useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Customer SLA views — powers the "SLA & Tickets" detail tab. Three data sources
 * (all verified live): the customer uptime dashboard (`/sla/dashboard/customers/:id`),
 * the customer's services (`/customers/:id/services`), and each service's SLA
 * config (`/services/:id/sla`). Field names are the v4 backend verbatim.
 */

/* ── Uptime overview ───────────────────────────────────────────────────── */

/** `GET /sla/dashboard/customers/:id` — aggregate uptime/SLA stats for the period. */
export interface CustomerUptimeStats {
  customer_id: number;
  total_services: number;
  period_minutes: number;
  total_sla_downtime_minutes: number;
  total_all_downtime_minutes: number;
  total_incidents: number;
  services_meeting_sla: number;
  services_breaching_sla: number;
  avg_uptime_percent: number;
}

export interface UptimeStatsParams {
  /** `YYYY-MM-DD` local dates; omit for the default (current month). */
  from_date?: string;
  to_date?: string;
}

/** `GET /sla/dashboard/customers/:id` — uptime overview band. */
export function useCustomerUptimeStats(customerId: number, params: UptimeStatsParams = {}) {
  return useQuery({
    queryKey: ['customers', customerId, 'uptime-stats', params],
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseObject<CustomerUptimeStats>> => {
      const query = qs.stringify(params);
      const res = await dataCentralApi.get<ResponseObject<CustomerUptimeStats>>(
        `/sla/dashboard/customers/${customerId}${query ? `?${query}` : ''}`,
      );
      return res.data;
    },
  });
}

/* ── Services (for the SLA cards grid) ─────────────────────────────────── */

/** A service row on `/customers/:id/services` (SLA-cards projection). */
export interface CustomerServiceRow {
  service_id: number;
  service_name: string;
  product_group_name?: string;
  service_status?: string;
  service_currency?: string;
  service_price_recurring?: number;
  service_start_date?: string;
  service_stopped_date?: string | null;
  contracts?: { contract_id: number; contract_number_id?: string }[];
}

export interface CustomerServicesParams {
  page?: number;
  page_size?: number;
}

/** `GET /customers/:id/services` — the customer's services (all, for SLA cards). */
export function useCustomerServicesForSla(customerId: number, params: CustomerServicesParams = {}) {
  return useQuery({
    queryKey: ['customers', customerId, 'sla-services', params],
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseArray<CustomerServiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<CustomerServiceRow>>(
        `/customers/${customerId}/services?${qs.stringify({ page: 1, page_size: 100, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── Per-service SLA config ────────────────────────────────────────────── */

/** `GET /services/:id/sla` — one service's SLA config (`ServiceSLA`). */
export interface ServiceSla {
  service_sla_id: number;
  service_id: number;
  sla_template_id?: number | null;
  sla_source?: string;
  is_active?: boolean;
  effective_from?: string;
  effective_to?: string;
  sla_rules?: unknown[];
  escalation_rules?: unknown[];
  contract_id?: number | null;
  custom_terms?: unknown[];
  template_name?: string;
  template_tier?: { sla_tier?: string; valid?: boolean } | null;
  template_default_response_time?: number;
  template_default_resolution_time?: number;
  effective_uptime_guarantee?: number;
  effective_downtime_allowance?: number;
  effective_business_hours_only?: boolean;
  effective_sla_rules?: unknown[];
  effective_escalation_rules?: unknown[];
  sla_status?: string;
}

/**
 * Fan out `GET /services/:id/sla` for a list of service ids (one query each,
 * mirroring the legacy `useQueries` fan-out). A 404 → `{ data: null }` (the
 * service has no SLA config yet). Returns a map keyed by service_id.
 */
export function useServiceSlas(serviceIds: number[]) {
  const results = useQueries({
    queries: serviceIds.map((serviceId) => ({
      queryKey: ['services', serviceId, 'sla'] as const,
      enabled: !!serviceId,
      retry: false,
      queryFn: async (): Promise<ServiceSla | null> => {
        try {
          const res = await dataCentralApi.get<ResponseObject<ServiceSla>>(`/services/${serviceId}/sla`);
          return res.data.result ?? null;
        } catch {
          return null; // no SLA configured (404) → render the "configure SLA" state
        }
      },
    })),
  });

  const byServiceId = new Map<number, ServiceSla | null>();
  const isLoading = results.some((r) => r.isLoading);
  serviceIds.forEach((id, i) => byServiceId.set(id, results[i]?.data ?? null));
  return { byServiceId, isLoading };
}
